<template>
<section :class="classes">
	<Heading :level="1" text="Claim Details" />
	<UserMessage v-if="error" message-type="error" :message-text="error" />
	<UserMessage v-if="!otherCriteriaValid && showStepErrors" message-type="error" message-text="Please upload at least one document" />
	<UserMessage v-if="!notItemsAdded && showStepsErrors" message-type="error" message-text="Please add at least one asset" />
	<form @submit.prevent="saveAndSubmit" novalidate ref="form">
		<CheckboxList
			name="activities"
			ref="activities"
			label-text="Please select which of the following activities you are associating to this claim"
			:options="activityList"
			:options-loading="activitiesLoading"
			:value="selectedActivities || []"
			@emitFieldData="getSelected"
		/>

		<UserMessage v-if="activitesError" message-type="error" :message-text="activitesError" />

		<HelpText classes="m-b-2">
			<template #text>
				<p class="minr-label m-t-4 text-bold">For any Capital items purchased above the value of £1,000 (excluding VAT), please provide the following asset information:</p>
				<ul>
					<li>Description of Item</li>
					<li>Purchase Date</li>
					<li>Serial Number</li>
					<li>Price (GBP)</li>
					<li>Life Span (Years)</li>
					<li>Location</li>
				</ul>
			</template>
		</HelpText>
		<child-entity-list
			:items="getItems('assets')"
			:item-type="itemType"
			@edit="editItem"
			@delete="deleteItem" />

		<Button class="secondary m-t-3 add-block icon-last" text="Add Asset" button-type="button" @btnClick="openPopUp(itemType)">
			<template #icon>
				<add-icon classes="minr-icon_sml" />
			</template>
		</Button>
		<pop-up @dismiss="dismissPopUp(itemType), setCurrentId('')" :popup-id="itemType" :show-close-btn="false">
			<template #popUpContent v-if="popUpOpen">
				<asset-fields :item-type="itemType" @finished="dismissPopUp(itemType), setCurrentId('')" />
			</template>
		</pop-up>

		<FileUpload
			ref="claimEvidence"
			name="claimEvidence"
			:is-multi="true"
			:is-required="!getAllUploadedFiles?.length"
			@fileUploadErrors="(val) => fileUploadError = val"
			@uploadFiles="uploadFiles()"
			@emitFieldData="v => prepareFiles(v)"
			:uploaded-files="getAllUploadedFiles"
			:upload-msg="uploadMsg"
			label-text="Please upload evidence documents - including Invoices/bank statements and any additional information you feel necessary"
			:help-text="{text: 'About 7 days before/after. When you have selected your files, please click the upload button below. your files will not be saved until you upload them.'}"
			required-error="Please select files to upload" />

		<HelpText classes="m-b-2">
			<template #text>
				<p class="m-t-4">
					I can confirm that in support of this claim, I have uploaded the following documents, in line with the requirements and process set out in the email titled 'Ready to Claim'
				</p>
				<Heading :level="1" text="Activities Selected" class="minr-h4 m-t-2" />
				<ul v-if="chosenActivities.length">
					<li v-for="(item, index) in chosenActivities" :key="index">
						{{ item }}
					</li>
				</ul>
				<p v-else>No activites selected</p>
				<p>
					Once we have completed the claim assessment of the documents you have uploaded, we will then contact you to advise the documents can then be posted to our office, the claim will then be paid following receipt of the identical documents.
				</p>
				<p>
					I understand that my claim cannot be processed until UMi Commercial Limited have confirmed that these documents meet the requirements as set out.
				</p>
			</template>
		</HelpText>

		<CheckboxField
			ref="ua_isyourbusinessvatregistered"
			name="ua_isyourbusinessvatregistered"
			label-text="Are you VAT Registered?"
			:true-value="1"
			:false-value="0"
			:value="formData.ua_isyourbusinessvatregistered"
			@emitFieldData="(val) => formData.ua_isyourbusinessvatregistered = val"
			:is-required="false"
			:options="[{ label: 'My business is VAT registered.', value: 'vat registered' }]" />

		<template v-if="isVATRegistered">
			<InputField
				name="ua_vatregistrationnumber"
				ref="ua_vatregistrationnumber"
				:help-text="{text: 'Your VAT number will usually start with GB followed by 9 digits e.g GB123456789. Please only enter the numbers in this field.'}"
				label-text="VAT Registration number"
				:value="formData.ua_vatregistrationnumber"
				@emitFieldData="(val) => formData.ua_vatregistrationnumber = val"
				required-error="Please enter your VAT Registration Number"
				:validation-rules="[rules.vatNum]" />
		</template>

		<CheckboxField
			ref="ua_declareinvoicesuploaded"
			name="ua_declareinvoicesuploaded"
			:label-text="checkboxLabels.ua_declareinvoicesuploaded"
			:value="parseBoolAsInt(formData.ua_declareinvoicesuploaded)"
			:true-value="1"
			:false-value="0"
			@emitFieldData="(val) => formData.ua_declareinvoicesuploaded = val"
			required-error="You must agree to this declaration to proceed" />

		<CheckboxField
			ref="ua_declarebankstatementsuploaded"
			name="ua_declarebankstatementsuploaded"
			:label-text="checkboxLabels.ua_declarebankstatementsuploaded"
			:value="parseBoolAsInt(formData.ua_declarebankstatementsuploaded)"
			:true-value="1"
			:false-value="0"
			@emitFieldData="(val) => formData.ua_declarebankstatementsuploaded = val"
			required-error="You must agree to this declaration to proceed" />

		<CheckboxField
			ref="ua_decnocash"
			name="ua_decnocash"
			:label-text="checkboxLabels.ua_decnocash"
			:value="parseBoolAsInt(formData.ua_decnocash)"
			:true-value="1"
			:false-value="0"
			@emitFieldData="(val) => formData.ua_decnocash = val"
			required-error="You must agree to this declaration to proceed" />

		<CheckboxField
			ref="ua_declarechequeevidenceuploaded"
			name="ua_declarechequeevidenceuploaded"
			:label-text="checkboxLabels.ua_declarechequeevidenceuploaded"
			:value="parseBoolAsInt(formData.ua_declarechequeevidenceuploaded)"
			:true-value="1"
			:false-value="0"
			@emitFieldData="(val) => formData.ua_declarechequeevidenceuploaded = val"
			required-error="You must agree to this declaration to proceed" />

		<CheckboxField
			ref="ua_declarebacsreportuploaded"
			name="ua_declarebacsreportuploaded"
			:label-text="checkboxLabels.ua_declarebacsreportuploaded"
			:value="parseBoolAsInt(formData.ua_declarebacsreportuploaded)"
			:true-value="1"
			:false-value="0"
			@emitFieldData="(val) => formData.ua_declarebacsreportuploaded = val"
			required-error="You must agree to this declaration to proceed" />

		<CheckboxField
			ref="ua_declarepaypalstatementuploaded"
			name="ua_declarepaypalstatementuploaded"
			:label-text="checkboxLabels.ua_declarepaypalstatementuploaded"
			:value="parseBoolAsInt(formData.ua_declarepaypalstatementuploaded)"
			:true-value="1"
			:false-value="0"
			@emitFieldData="(val) => formData.ua_declarepaypalstatementuploaded = val"
			required-error="You must agree to this declaration to proceed" />

		<CheckboxField
			ref="ua_declarecreditcardstatementuploaded"
			name="ua_declarecreditcardstatementuploaded"
			:label-text="checkboxLabels.ua_declarecreditcardstatementuploaded"
			:value="parseBoolAsInt(formData.ua_declarecreditcardstatementuploaded)"
			:true-value="1"
			:false-value="0"
			@emitFieldData="(val) => formData.ua_declarecreditcardstatementuploaded = val"
			required-error="You must agree to this declaration to proceed" />

		<CheckboxField
			ref="ua_declareactivityexpenditurefullydefrayed"
			name="ua_declareactivityexpenditurefullydefrayed"
			:label-text="checkboxLabels.ua_declareactivityexpenditurefullydefrayed"
			:value="parseBoolAsInt(formData.ua_declareactivityexpenditurefullydefrayed)"
			:true-value="1"
			:false-value="0"
			@emitFieldData="(val) => formData.ua_declareactivityexpenditurefullydefrayed = val"
			required-error="You must agree to this declaration to proceed" />

		<CheckboxField
			ref="ua_declareinfotrueandcomplete"
			name="ua_declareinfotrueandcomplete"
			:label-text="checkboxLabels.ua_declareinfotrueandcomplete"
			:value="parseBoolAsInt(formData.ua_declareinfotrueandcomplete)"
			:true-value="1"
			:false-value="0"
			@emitFieldData="(val) => formData.ua_declareinfotrueandcomplete = val"
			required-error="You must agree to this declaration to proceed" />

		<CheckboxField
			ref="ua_declareprojectcomplete"
			name="ua_declareprojectcomplete"
			:label-text="checkboxLabels.ua_declareprojectcomplete"
			:value="parseBoolAsInt(formData.ua_declareprojectcomplete)"
			:true-value="1"
			:false-value="0"
			@emitFieldData="(val) => formData.ua_declareprojectcomplete = val"
			required-error="You must agree to this declaration to proceed" />

		<CheckboxField
			ref="ua_dectruecopyoforiginals"
			name="ua_dectruecopyoforiginals"
			:label-text="checkboxLabels.ua_dectruecopyoforiginals"
			:value="parseBoolAsInt(formData.ua_dectruecopyoforiginals)"
			:true-value="1"
			:false-value="0"
			@emitFieldData="(val) => formData.ua_dectruecopyoforiginals = val"
			required-error="You must agree to this declaration to proceed" />

		<TextArea
			ref="ua_othercommentsforclaimassessment"
			name="ua_othercommentsforclaimassessment"
			:value="formData.ua_othercommentsforclaimassessment"
			@emitFieldData="(val) => formData.ua_othercommentsforclaimassessment = val"
			:max-chars="2000"
			label-text="Please include any other comments that you wish to be taken into account as part of the assessment of your grant claim:"
			:is-required="false"
			:validation-rules="[(v) => rules.containsLength(v, 50, 2000)]" />
	</form>
</section>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

import { formSteps } from '@/mixins/claimFormSteps';
import { formValidator } from '@/mixins/formValidator';
import { validationRules } from '@/utilities/validationRules';
import { fileUploads } from '@/mixins/fileUploads';

import AssetFields from '@/components/ChildEntities/Fields/AssetFields.vue';
import { childEntityParent } from '@/mixins/childEntityParent';

export default {
	name: 'ClaimDetails',
	components: {
		AssetFields
	},
	mixins: [formValidator, validationRules, formSteps, fileUploads, childEntityParent],
	provide: {
		otherFieldId: '',
		itemIdentifier: 'ua_description',
		itemIdField: 'ua_icassetid'
	},
	props: {
		classes: {
			type: String,
			default: ''
		},
		applicationSize: {
			type: String,
			default: 'large',
			validator (value) {
				return ['small', 'large'].includes(value);
			}
		}
	},
	data () {
		return {
			rules: validationRules,
			error: '',
			itemType: 'assets',
			activitiesLoading: true,
			selectedActivities: [],
			formData: {
				ua_declareinvoicesuploaded: false,
				ua_declarebankstatementsuploaded: false,
				ua_decnocash: false,
				ua_declarechequeevidenceuploaded: false,
				ua_declarebacsreportuploaded: false,
				ua_declarepaypalstatementuploaded: false,
				ua_declarecreditcardstatementuploaded: false,
				ua_declareactivityexpenditurefullydefrayed: false,
				ua_declareinfotrueandcomplete: false,
				ua_declareprojectcomplete: false,
				ua_dectruecopyoforiginals: false,
				ua_othercommentsforclaimassessment: '',
				ua_isyourbusinessvatregistered: 0,
				ua_vatregistrationnumber: ''

			},
			chosenActivities: [],
			noteSubject: 'CLAIM_EVIDENCE',
			checkboxLabels: {
				ua_declareinvoicesuploaded: 'Invoice(s) for expenditure incurred',
				ua_declarebankstatementsuploaded: 'Bank Statements which clearly show: the business account name, the business account number, the sort code, the relevant payment or payments (for project expenditure evidenced through invoices) leaving the account, all transactions 7 days before and 7 days after the relevant payment(s) clearing',
				ua_decnocash: 'I understand that Cash payments to the supplier cannot be excepted',
				ua_declarechequeevidenceuploaded: 'If the payment to the supplier is made by Cheque, a copy of the cheque and/or cheque stub showing the Supplier name and amount paid.',
				ua_declarebacsreportuploaded: 'If the payment to the supplier is made by BACS, a copy of the BACS report with the relevant payment or payments highlighted.',
				ua_declarepaypalstatementuploaded: 'If the payment to the supplier is made through PayPal, a copy of the PayPal statement with the relevant payment or payments highlighted.',
				ua_declarecreditcardstatementuploaded: 'If the payment to the supplier is made by Credit Card, a copy of the credit card statement showing the FULL payment of the item, along with the bank statement (criteria above) to show a payment being made to the credit card.',
				ua_declareactivityexpenditurefullydefrayed: 'I can confirm that the activity expenditure has been incurred and defrayed in full prior to the completion of this claims form.',
				ua_declareinfotrueandcomplete: 'I declare that the information included in this claim form is true and complete to the best of my knowledge.',
				ua_declareprojectcomplete: 'I can confirm that I have completed the project that was set out in my application for this grant, including the activities and expenditure as they were described.',
				ua_dectruecopyoforiginals: 'I certify that all documents provided as part of this claim are true copies of the originals'

			}
		};
	},
	computed: {
		noItemsAdded () {
			// assets are only required if cost is over 1k
			return false;
		},
		otherCriteriaValid () {
			return this.getUploadedFiles && this.getUploadedFiles.length;
		},
		isVATRegistered () {
			return this.formData.ua_isyourbusinessvatregistered === 1;
		},
		...mapGetters('childEntities', ['getEligibleActivities']),
		...mapGetters('application', ['getApplicationId'])
	},
	mounted () {
		const assets = this.getItems('assets');
		const payload = {
			ua_icapplication: this.getApplicationId(this.$route.params.size),
			ua_icclaim: this.$route.params.claimId
		};
		if (!assets.length) {
			this.fetchAssetItems(payload).catch((err) => {
				if (err.status === 422 || err.status === 500) {
					this.fetchItemsMsg.type = err.type;
					this.fetchItemsMsg.text = err.message;
				}
			});
		}
	},
	methods: {
		...mapActions('childEntities', ['fetchAssetItems'])
	}
};
  </script>
